import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { LIST_INSUGO_LEADS } from "../../config/urls";
import AppTable from "../../common/app-table";
import typography from "../../cssUtils/typography";
import { formatWithTzLabel } from "../../utils/dateUtils";

const COLUMNS = [
  {
    label: "Name",
    id: "name",
    render: (row) => (
      <Typography
        sx={{
          ...typography.bodyLarge,
          fontWeight: 600,
          color: "#1D2330",
          cursor: "default",
          width: "fit-content",
        }}
      >
        {row.name}
      </Typography>
    ),
  },
  {
    label: "Phone Number",
    id: "phone_no",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.phone_no || "N/A"}
      </Typography>
    ),
  },
  {
    label: "Email",
    id: "email",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.email || "N/A"}
      </Typography>
    ),
  },
  {
    label: "City",
    id: "city",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.city || "N/A"}
      </Typography>
    ),
  },
  {
    label: "State",
    id: "state",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.state || "N/A"}
      </Typography>
    ),
  },
  {
    label: "Insurance Type",
    id: "insurance_type",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.insurance_type || "N/A"}
      </Typography>
    ),
  },
  {
    label: "Query",
    id: "query",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.query || "N/A"}
      </Typography>
    ),
  },
  {
    label: "Last Updated",
    id: "updated_at",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.updated_at_iso ? formatWithTzLabel(row.updated_at_iso) : "N/A"}
      </Typography>
    ),
  },
];

const Leads = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    const token = await getValidToken();

    setLoading(true);

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": tenant,
      };

      const response = await fetch(LIST_INSUGO_LEADS, { headers });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      setLeads(data.results);
      setTotalCount(data.count || 0);
    } catch (error) {
      console.error("Error fetching leads:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#fafafa" }}>
      <AppTable
        columns={COLUMNS}
        rows={leads}
        loading={loading}
        emptyMessage="No Leads Found"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        paperSx={{ border: "1px solid #DCDFE5", borderRadius: "12px", mb: 2 }}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
        }}
        rowSx={(row, index) => ({
          backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
          cursor: "default",
          "&:hover": { backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC" },
          "& td": { borderBottom: "none", borderTop: "none" },
        })}
      />
    </Box>
  );
};

export default Leads;
